import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";

import { CertificateBadge } from "@/components/pages_logged_in/certifications/CertificateBadge";

// Helper function to register fonts - can be called from different contexts
export function registerCertificateFonts(fontPaths: {
  regular: string;
  bold: string;
}) {
  Font.register({
    family: "Inter",
    fonts: [
      {
        src: fontPaths.regular,
        fontWeight: 400,
      },
      {
        src: fontPaths.bold,
        fontWeight: 800,
      },
    ],
  });
}

// Auto-register fonts when imported in browser context
if (typeof window !== "undefined") {
  // Dynamic imports for browser context
  registerCertificateFonts({
    regular: new URL("./Inter-Regular.ttf", import.meta.url).href,
    bold: new URL("./Inter-ExtraBold.ttf", import.meta.url).href,
  });
}

const styles = StyleSheet.create({
  page: {
    backgroundColor: "white",
    paddingHorizontal: 80,
  },
  section: {
    height: "100%",
    color: "#2E3C47",
    alignItems: "center",
    justifyContent: "center",
    gap: 24,
  },
  title: {
    fontSize: 36,
    fontFamily: "Inter",
    fontWeight: 400,

    textAlign: "center",
  },
  subtitle: {
    fontSize: 24,
    fontFamily: "Inter",
    fontWeight: 400,
  },
  hr: {
    width: "100%",
    height: 1,
    backgroundColor: "#E2E2E2",
  },
  description: {
    fontSize: 16,
    fontFamily: "Inter",
    fontWeight: 400,
    color: "#778397",
    textAlign: "center",
    lineHeight: 1.75,
  },
  validity: {
    fontSize: 16,
    fontFamily: "Inter",
    fontWeight: 400,
    color: "#778397",
  },
  logo: {
    width: 20,
    height: 20,
  },
  companyName: {
    fontSize: 16,
    fontFamily: "Inter",
    fontWeight: 800,
  },
  companyInfo: {
    flexDirection: "row",
    alignItems: "baseline",
    gap: 12,
  },
  header: {
    alignItems: "center",
    gap: 24,
  },
  footer: {
    alignItems: "center",
    gap: 24,
  },
});

export function CertificatePDF({
  name,
  recipientName = "Recipient Name",
  description = "",
  validity = "",
  logoUrl,
  companyName,
}: {
  name: string;
  recipientName?: string;
  description?: string;
  validity?: string;
  logoUrl?: string | null;
  companyName?: string;
}) {
  return (
    <Document title={`${name} - ${recipientName}`}>
      <Page size="LETTER" style={styles.page} orientation="landscape">
        <View style={styles.section}>
          <CertificateBadge />
          <Text style={styles.title}>{name}</Text>
          <View style={styles.hr} />
          <Text style={styles.subtitle}>{recipientName}</Text>
          <Text style={styles.description}>{description}</Text>

          <Text style={styles.validity}>{validity}</Text>
          <View style={styles.hr} />
          <View style={styles.companyInfo}>
            {logoUrl && (
              <Image
                src={{
                  uri: logoUrl,
                  method: "GET",
                  headers: { "Cache-Control": "no-cache" },
                }}
                style={styles.logo}
              />
            )}
            {companyName && (
              <Text style={styles.companyName}>{companyName}</Text>
            )}
          </View>
        </View>
      </Page>
    </Document>
  );
}
